import { useState } from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Media, PageHero, Reveal, SectionHeading } from "@/components/site/Primitives";
import { TourInquiryForm } from "@/components/site/TourInquiryForm";
import { IMAGES } from "@/lib/site";
import { TOUR_PLANS } from "@/lib/content";

const PlanCard = ({ p, i, onChoose }) => (
  <Reveal delay={i * 0.1}>
    <article className="group flex h-full flex-col" data-testid={`tour-plan-${p.id}`}>
      <Media img={p.img} className="aspect-[4/3]" imgClassName="transition-transform duration-1000 group-hover:scale-105" />
      <h3 className="mt-6 font-serif text-3xl text-ink">{p.title}</h3>
      <p className="mt-2 flex-1 text-slate-600">{p.text}</p>
      <button type="button" onClick={() => onChoose(p.id)} className="mt-6 inline-flex w-fit items-center gap-2 text-sm font-medium text-ink underline decoration-gold underline-offset-[6px] hover:text-gold-hover" data-testid={`tour-plan-choose-${p.id}`}>
        Choose this tour <ArrowRight className="h-4 w-4" />
      </button>
    </article>
  </Reveal>
);

export default function Tours() {
  const [tourType, setTourType] = useState("");
  const choose = (id) => {
    setTourType(id);
    document.getElementById("plan")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Seo
        title="Rhodes Private Tours | Lindos & Butterfly Valley"
        description="Private Rhodes tours from the city to Lindos or the Valley of the Butterflies, or a custom tour planned around you. Flexible, with a local driver."
        path="/tours"
      />
      <PageHero
        eyebrow="Private tours"
        title="Explore Rhodes in style"
        text="Private, flexible tours with acknowledged local drivers. Pick one of our tours or let us plan a custom day around you."
        img={IMAGES.tours}
      >
        <a href="#tours" className="btn btn-gold" data-testid="tours-plan-button">See the tours</a>
        <span className="inline-flex min-h-[48px] items-center gap-2 rounded-full border border-gold-border bg-gold-light px-5 text-sm text-ink" data-testid="tours-certified-drivers-badge">
          <ShieldCheck className="h-4 w-4 text-gold" /> Acknowledged drivers
        </span>
      </PageHero>

      <section id="tours" className="container-x section-y scroll-mt-24" data-testid="tour-plans-section">
        <SectionHeading eyebrow="Choose your tour" title="Two ready-made tours, or your own" text="Every tour is with our acknowledged drivers, who know the island inside out." />
        <div className="mt-14 grid gap-x-5 gap-y-12 md:grid-cols-3">
          {TOUR_PLANS.map((p, i) => <PlanCard key={p.id} p={p} i={i} onChoose={choose} />)}
        </div>
      </section>

      <section id="plan" className="container-x pb-20 scroll-mt-24 md:pb-28" data-testid="tour-plan-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="Request a tour" title="Book your tour" />
            <Reveal delay={0.1} className="card-soft mt-8 border border-gold-border bg-gold-light p-6" data-testid="tour-price-note">
              <p className="font-serif text-2xl text-ink">Price depends on your itinerary and duration.</p>
              <p className="mt-3 text-sm text-slate-600">Send us a few details and we will reply with a clear, fixed price.</p>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-8"><TourInquiryForm tourType={tourType} /></Reveal>
        </div>
      </section>
    </>
  );
}
